import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

// Import modals
import AddUserModal from '../modals/AddUserModal';
import EditUserModal from '../modals/EditUserModal';
import ConfirmDeleteModal from '../../shared/modals/ConfirmDeleteModal';

const UserManagement = () => {
  const routes = all_routes;
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Mock users data
  const users = [
    {
      id: '1',
      name: 'John Smith',
      email: 'john.smith@school.edu',
      role: 'Teacher',
      status: 'Active',
      joinDate: '2024-01-15'
    },
    {
      id: '2',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@school.edu',
      role: 'Student',
      status: 'Active',
      joinDate: '2024-02-20'
    },
    {
      id: '3',
      name: 'Mike Wilson',
      email: 'mike.wilson@school.edu',
      role: 'Parent',
      status: 'Inactive',
      joinDate: '2024-03-10'
    }
  ];

  const filteredUsers = users; // Add filtering logic here

  const getStatusBadgeClass = (status: string) => {
    return status === 'Active' ? 'badge-soft-success' : 'badge-soft-danger';
  };

  const handleEditUser = (userId: string) => {
    setSelectedUserId(userId);
  };

  const handleDeleteUser = (user: any) => {
    setUserToDelete(user);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    
    setIsDeleting(true);
    try {
      // TODO: API call to delete user
      console.log('Deleting user:', userToDelete);
      // Close modal after successful delete
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Admin Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">User Management</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Page Title */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h4 className="card-title">User Management System</h4>
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-primary btn-sm"
                      data-bs-toggle="modal" 
                      data-bs-target="#addUserModal"
                    >
                      <i className="ti ti-plus me-1"></i>Add User
                    </button>
                    <div className="dropdown">
                      <button className="btn btn-outline-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                        Actions
                      </button>
                      <ul className="dropdown-menu">
                        <li><button className="dropdown-item" disabled={selectedUsers.length === 0}>
                          <i className="ti ti-edit me-2"></i>Bulk Edit
                        </button></li>
                        <li><button className="dropdown-item text-danger" disabled={selectedUsers.length === 0}>
                          <i className="ti ti-trash me-2"></i>Delete Selected
                        </button></li>
                        <li><hr className="dropdown-divider" /></li>
                        <li><button className="dropdown-item">
                          <i className="ti ti-download me-2"></i>Export Users
                        </button></li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* TODO Placeholders */}
                <div className="card-body border-bottom">
                  <div className="alert alert-info">
                    <h6><i className="ti ti-info-circle me-2"></i>TODO: Connect to user management API</h6>
                    <ul className="mb-0">
                      <li>Fetch users from database</li>
                      <li>Add/Edit/Delete user functionality</li>
                      <li>Role assignment functionality</li>
                      <li>User status management (Active/Inactive)</li>
                      <li>Bulk operations and CSV export</li>
                    </ul>
                  </div>
                </div>

                {/* Users Table */}
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>
                            <div className="form-check">
                              <input className="form-check-input" type="checkbox" />
                            </div>
                          </th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Join Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((user) => (
                          <tr key={user.id}>
                            <td>
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" />
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-sm me-2">
                                  <span className="avatar-title bg-primary-transparent text-primary">
                                    {user.name.charAt(0)}
                                  </span>
                                </div>
                                <h6 className="mb-0">{user.name}</h6>
                              </div>
                            </td>
                            <td>{user.email}</td>
                            <td>
                              <span className="badge badge-soft-primary">{user.role}</span>
                            </td>
                            <td>
                              <span className={`badge ${getStatusBadgeClass(user.status)}`}>
                                {user.status}
                              </span>
                            </td>
                            <td>{user.joinDate}</td>
                            <td>
                              <div className="dropdown">
                                <button className="btn btn-outline-light btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                                  Actions
                                </button>
                                <ul className="dropdown-menu">
                                  <li>
                                    <button 
                                      className="dropdown-item"
                                      data-bs-toggle="modal" 
                                      data-bs-target="#editUserModal"
                                      onClick={() => handleEditUser(user.id)}
                                    >
                                      <i className="ti ti-edit me-2"></i>Edit User
                                    </button>
                                  </li>
                                  <li>
                                    <button className="dropdown-item">
                                      <i className="ti ti-eye me-2"></i>View Details
                                    </button>
                                  </li>
                                  <li>
                                    <button className="dropdown-item">
                                      <i className="ti ti-key me-2"></i>Reset Password
                                    </button>
                                  </li>
                                  <li>
                                    <button className="dropdown-item">
                                      <i className="ti ti-power me-2"></i>
                                      {user.status === 'Active' ? 'Deactivate' : 'Activate'}
                                    </button>
                                  </li>
                                  <li>
                                    <button 
                                      className="dropdown-item text-danger"
                                      data-bs-toggle="modal" 
                                      data-bs-target="#confirmDeleteModal"
                                      onClick={() => handleDeleteUser(user)}
                                    >
                                      <i className="ti ti-trash me-2"></i>Delete User
                                    </button>
                                  </li>
                                </ul>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <p className="text-muted mb-0">Showing {filteredUsers.length} of {users.length} users</p>
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className="page-item disabled">
                          <button className="page-link">Previous</button>
                        </li>
                        <li className="page-item active">
                          <button className="page-link">1</button>
                        </li>
                        <li className="page-item">
                          <button className="page-link">2</button>
                        </li>
                        <li className="page-item">
                          <button className="page-link">Next</button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddUserModal />
      <EditUserModal userId={selectedUserId} />
      <ConfirmDeleteModal 
        itemName={userToDelete?.name}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
      />
    </>
  );
};

export default UserManagement;
